import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { validateUrl, deviceSwitcherKey } from "@/utils/home";
import NextImage from "../NextImage";
import { event_banner_clicked } from "@/utils/gtm";

// Function to check if a banner is active
const isBannerActive = (banner, isMobile) => {
  const isActive = banner?.[isMobile ? "mobile-active" : "active"];
  // Get the current date and convert it to UTC+3
  const currentDate = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Riyadh" })
  );
  const startDate = new Date(banner["start-date"]?.replace(" ", "T"));
  const endDate = new Date(banner["end-date"]);

  return (
    isActive &&
    currentDate.getTime() >= startDate.getTime() &&
    currentDate.getTime() <= endDate.getTime()
  );
};
const MainSlider2 = ({ data, isMobile }) => {
  const { locale } = useRouter();
  const position = isMobile ? "mobile-position" : "position";
  const slides = (Array.isArray(data.value) ? data.value : [])
    ?.filter((slide) => isBannerActive(slide, isMobile))
    ?.sort((a, b) => +a?.[position] - +b?.[position]);
  // console.log("slides ------------ MainSlider2", slides);
  return <Carousel {...{ slides, isMobile, locale }} />;
};

MainSlider2.displayName = "BannerSwiper";
export default MainSlider2;

const SLIDE_TRANSITION_MS = 700;

const Carousel = ({ slides, isMobile, locale }) => {
  const slideCount = slides.length;
  const isLooping = slideCount > 1;
  // Track holds [clone of last, ...slides, clone of first] so both wrap
  // directions are a single-step slide; position is the index in that track.
  const [position, setPosition] = useState(isLooping ? 1 : 0);
  const [isMoving, setIsMoving] = useState(false);
  const [withTransition, setWithTransition] = useState(true);
  const [startX, setStartX] = useState(0);
  const [deltaX, setDeltaX] = useState(0);

  const currentSlide = isLooping ? (position - 1 + slideCount) % slideCount : 0;

  const moveTo = (target) => {
    if (!isLooping || isMoving || target === position) return;
    setWithTransition(true);
    setIsMoving(true);
    setPosition(target);
  };

  const handleNext = () => moveTo(position + 1);
  const handlePrev = () => moveTo(position - 1);
  const goToSlide = (index) => moveTo(index + 1);

  // After landing on a clone, jump to its real slide with the transition off.
  const settle = () => {
    setIsMoving(false);
    if (position === 0 || position === slideCount + 1) {
      setWithTransition(false);
      setPosition(position === 0 ? slideCount : 1);
    }
  };

  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    if (isMoving) settle();
  };

  // transitionend can be skipped (e.g. background tab); never stay locked.
  useEffect(() => {
    if (!isMoving) return;
    const fallback = setTimeout(settle, SLIDE_TRANSITION_MS + 100);
    return () => clearTimeout(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoving, position]);

  useEffect(() => {
    if (!isLooping) return;
    const timer = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, isMoving, isLooping]);

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setDeltaX(e.touches[0].clientX - startX);
  };

  const handleTouchEnd = () => {
    if (deltaX > 50) handlePrev();
    if (deltaX < -50) handleNext();
    setDeltaX(0);
  };

  const isRtl = locale === "ar";
  const trackOffset = isRtl ? position * 100 : -position * 100;
  const trackSlides = isLooping
    ? [
        {
          slide: slides[slideCount - 1],
          realIndex: slideCount - 1,
          isClone: true,
        },
        ...slides.map((slide, realIndex) => ({
          slide,
          realIndex,
          isClone: false,
        })),
        { slide: slides[0], realIndex: 0, isClone: true },
      ]
    : slides.map((slide, realIndex) => ({ slide, realIndex, isClone: false }));

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative w-full bg-black/80 mx-auto max-[768px]:aspect-[6/4] aspect-[18/5] overflow-hidden group"
    >
      {/* Slides */}
      <div
        className={`flex h-full w-full ${
          withTransition ? "transition-transform duration-700 ease-in-out" : ""
        }`}
        style={{ transform: `translateX(${trackOffset}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {trackSlides.map(({ slide, realIndex, isClone }, trackIndex) => {
          const url = validateUrl(slide.url, slide.type);
          const langPreFex = locale === "ar" ? "" : "/en";
          const bannerFullPath = `${process.env.NEXT_PUBLIC_WEBSITE_URL}${langPreFex}/${url}`;
          const isEager = !isClone && realIndex < 2;
          return (
            <Link
              href={url}
              key={`main-slider-${isClone ? `clone-${trackIndex}` : realIndex}`}
              className="relative w-full h-full shrink-0"
              aria-hidden={isClone || undefined}
              tabIndex={isClone ? -1 : undefined}
              onClick={() =>
                event_banner_clicked({
                  banner_name: slide[`title-${locale}`] || "",
                  banner_image_url:
                    slide?.[deviceSwitcherKey("img", isMobile, locale)],
                  banner_destination_url: bannerFullPath,
                })
              }
            >
              <NextImage
                priority={isEager}
                loading={isEager ? "eager" : "lazy"}
                width={isMobile ? 600 : 1800}
                height={isMobile ? 400 : 500}
                src={slide?.[deviceSwitcherKey("img", isMobile, locale)]}
                alt={slide.title}
                className="object-cover w-full h-full"
              />
            </Link>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <div
        className={`group-hover:opacity-100 opacity-0 transition-opacity duration-200 max-[570px]:hidden block`}
      >
        <button
          onClick={() => (locale === "ar" ? handleNext() : handlePrev())}
          className="absolute z-10 p-4 text-white transition-all -translate-y-1/2 rounded-full left-4 top-1/2 bg-black/50 hover:bg-black/75"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={() => (locale === "en" ? handleNext() : handlePrev())}
          className="absolute z-50 p-4 text-white transition-all -translate-y-1/2 rounded-full right-4 top-1/2 bg-black/50 hover:bg-black/75"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Dots Navigation */}
      <div className="absolute z-10 flex gap-2 -translate-x-1/2 bottom-4 left-1/2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`max-[768px]:size-2 w-3 h-3 rounded-full transition-all duration-300 ${
              currentSlide === index
                ? "bg-white max-[768px]:w-4 w-6"
                : "bg-white/50 hover:bg-white/75"
            }`}
          />
        ))}
      </div>
    </div>
  );
};
